"use client";

import { useEffect, useRef, useState } from "react";
import type { Locale } from "@/lib/api";
import { fmtMoney } from "@/lib/i18n";
import { pushDataLayer } from "@/lib/data-layer";

const P = "RoadTaxCalculatorPage-module__fLbqYG__";

// Категория (Euro-стандарт + топливо) → надбавка (значение = слагаемое к налогу).
type EuroClass =
  | "euro6"
  | "euro5_petrol"
  | "euro5_diesel"
  | "euro4_petrol"
  | "euro4_diesel";

const CATEGORIES: Record<Locale, [EuroClass, number, string][]> = {
  en: [
    ["euro6", 0, "Euro 6"],
    ["euro5_petrol", 100, "Euro 5 (Petrol, from 2009 to 2014)"],
    ["euro5_diesel", 250, "Euro 5 (Diesel, from 2009 to 2014)"],
    ["euro4_petrol", 300, "Euro 4 (Petrol, from 2005 to 2009)"],
    ["euro4_diesel", 600, "Euro 4 (Diesel, from 2005 to 2009)"],
  ],
  ru: [
    ["euro6", 0, "Euro 6"],
    ["euro5_petrol", 100, "Euro 5 (бензин, с 2009 по 2014)"],
    ["euro5_diesel", 250, "Euro 5 (дизель, с 2009 по 2014)"],
    ["euro4_petrol", 300, "Euro 4 (бензин, с 2005 по 2009)"],
    ["euro4_diesel", 600, "Euro 4 (дизель, с 2005 по 2009)"],
  ],
};

const STR = {
  en: {
    title: "Road Tax Calculator",
    desc: "Choose the emissions category and enter CO₂ to get an estimated annual total.",
    category: "Vehicle category",
    co2: "CO₂ emissions",
    placeholder: "Enter CO₂ in g/km",
    result: "Total annual road tax",
    note: "The CO₂ tax is capped at €1,500. For vehicles manufactured before September 2014, select the correct Euro standard and fuel type because the age/fuel surcharge is added separately.",
  },
  ru: {
    title: "Калькулятор налога",
    desc: "Выберите экологический класс и введите значение CO₂ для расчета ориентировочной суммы за год.",
    category: "Категория автомобиля",
    co2: "Выбросы CO₂",
    placeholder: "Введите CO₂ в г/км",
    result: "Годовой дорожный налог",
    note: "Налог по CO₂ ограничен €1 500. Для автомобилей до сентября 2014 года выберите стандарт Euro и тип топлива: возрастная надбавка добавляется отдельно.",
  },
};

function contentString(value: unknown, fallback: string): string {
  return typeof value === "string" ? value : fallback;
}

function contentNumber(value: unknown, fallback: number): number {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

// Налог по CO₂ (кипрские банды, снято с живого калькулятора прода):
//  ≤120 → ×0.5; 121–150 → 60+(co2−120)×3; 151–180 → 150+(co2−150)×5; >180 → 300+(co2−180)×10; cap €1,500.
function baseTax(co2: number): number {
  let tax = 0;
  if (co2 <= 120) tax = co2 * 0.5;
  else if (co2 <= 150) tax = 60 + (co2 - 120) * 3;
  else if (co2 <= 180) tax = 150 + (co2 - 150) * 5;
  else tax = 300 + (co2 - 180) * 10;
  return Math.min(1500, tax);
}

export function RoadTaxCalculator({
  locale = "en",
  content,
}: {
  locale?: Locale;
  content?: Record<string, unknown>;
}) {
  const [euroClass, setEuroClass] = useState<EuroClass>("euro6");
  const [co2, setCo2] = useState("");
  const lastReportedSignature = useRef("");
  const defaults = STR[locale] ?? STR.en;
  const s = {
    title: contentString(content?.title, defaults.title),
    desc: contentString(content?.description, defaults.desc),
    category: contentString(content?.selectLabel, defaults.category),
    co2: contentString(content?.inputLabel, defaults.co2),
    placeholder: contentString(content?.inputPlaceholder, defaults.placeholder),
    result: contentString(content?.resultLabel, defaults.result),
    note: contentString(content?.note, defaults.note),
  };
  const defaultCats = CATEGORIES[locale] ?? CATEGORIES.en;
  const configuredOptions = Array.isArray(content?.options)
    ? content.options.slice(0, defaultCats.length).map(record)
    : [];
  const cats = defaultCats.map(([key, surcharge, label], index) => {
    const configured = configuredOptions[index];
    return [
      key,
      contentNumber(configured?.value, surcharge),
      contentString(configured?.label, label),
    ] as [EuroClass, number, string];
  });
  const categorySurcharge =
    cats.find(([key]) => key === euroClass)?.[1] ?? 0;

  const n = parseFloat(co2);
  const total =
    co2 === "" || isNaN(n) || n < 0
      ? 0
      : baseTax(n) + categorySurcharge;
  const fmt = Number.isInteger(total)
    ? fmtMoney(total, locale)
    : `€${total.toLocaleString(locale === "ru" ? "ru-RU" : "en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

  useEffect(() => {
    if (co2 === "" || !Number.isFinite(n) || n < 0) return;
    const timer = window.setTimeout(() => {
      const signature = `${n}:${euroClass}:${total}`;
      if (lastReportedSignature.current === signature) return;
      lastReportedSignature.current = signature;
      pushDataLayer({
        event: "calc_roadtax_done",
        co2: n,
        euro_class: euroClass,
        result_eur: total,
      });
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [co2, euroClass, n, total]);

  return (
    <aside className={`${P}calculatorCard`}>
      <div className={`${P}calculatorHead`}>
        <p className={`${P}calculatorEyebrow`}>CO₂</p>
        <h2 className={`${P}calculatorTitle`}>{s.title}</h2>
        <p className={`${P}calculatorDescription`}>{s.desc}</p>
      </div>
      <div className={`${P}calculatorBody`}>
        <label className={`${P}field`}>
          <span className={`${P}fieldLabel`}>{s.category}</span>
          <select
            className={`${P}fieldControl`}
            value={euroClass}
            onChange={(e) => setEuroClass(e.target.value as EuroClass)}
          >
            {cats.map(([key, _surcharge, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className={`${P}field`}>
          <span className={`${P}fieldLabel`}>{s.co2}</span>
          <input
            className={`${P}fieldControl`}
            min="0"
            step="1"
            inputMode="numeric"
            placeholder={s.placeholder}
            type="number"
            value={co2}
            onChange={(e) => setCo2(e.target.value)}
          />
        </label>
      </div>
      <div className={`${P}resultCard`}>
        <span className={`${P}resultLabel`}>{s.result}</span>
        <strong className={`${P}resultValue`}>{fmt}</strong>
      </div>
      <p className={`${P}calculatorNote`}>{s.note}</p>
    </aside>
  );
}
